import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Video, FolderOpen, Loader2, CheckCircle2, AlertCircle, ShieldAlert, Clock } from "lucide-react"
import {
  uploadVideo, browserVideoUploadDeps, readVideoDuration, abortableSleep, discardVideo, forgetVideo, pruneDoneVideos, videoFileKey,
} from "@/lib/videoUpload"
import {
  prepareSelection, runVideoBatch, retryOrder, summarizeBatch, batchLocksSupported, browserWithLock,
  VIDEO_BATCH_MAX_FILES, type BatchItem, type BatchState, type BatchStop, type BatchDeps,
} from "@/lib/videoBatch"

// Hotel video upload (PLAN.md §G, §K; several at once: PLAN-multivideo.md). Shown only when the server says this
// hotel may upload videos (capability). Consent is mandatory (Mike 2026-10-16) and covers the whole selection
// (Mike 2026-10-30); the hint about guests is always visible.
export const GUEST_HINT =
  "Bitte filmen Sie Gäste nur, wenn sie zugestimmt haben oder nicht erkennbar sind. Keine Kinder ohne Einwilligung der Eltern."

const mb = (n: number) => `${(n / (1024 * 1024)).toFixed(n < 100 * 1024 * 1024 ? 1 : 0)} MB`

const STATUS_TEXT: Record<BatchItem["status"], string> = {
  waiting: "Wartet",
  checking: "Wird geprüft…",
  uploading: "Wird hochgeladen…",
  done: "Angekommen, wird aufbereitet",
  already: "Bereits hochgeladen",
  failed: "Fehlgeschlagen",
  skipped: "Übersprungen",
  cancelled: "Abgebrochen",
  not_started: "Nicht hochgeladen",
}

export function VideoUploadPanel({
  basePath, accessKey, message, pillarName, onDone, batchEnabled = false, batchAllowed,
}: {
  /** "/api/upload/video" (signed in) or "/api/upload/<token>/video" (upload link) */
  basePath: string
  accessKey: string
  message?: string
  pillarName?: string
  /** Called once after a run in which at least one video arrived. */
  onDone?: () => void
  /** Server capability: several videos per selection (HOTEL_VIDEO_BATCH_ENABLED). */
  batchEnabled?: boolean
  /** Asked again before every further file of a batch; false/throw = stop after the current file. */
  batchAllowed?: (signal: AbortSignal) => Promise<boolean>
}) {
  const [consent, setConsent] = useState(false)
  const [items, setItems] = useState<BatchItem[]>([])
  const [running, setRunning] = useState(false)
  const [state, setState] = useState<BatchState | null>(null)
  const [stop, setStop] = useState<BatchStop | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyRow, setBusyRow] = useState<number | null>(null)
  const recordRef = useRef<HTMLInputElement>(null)
  const pickRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const mounted = useRef(true)
  const multiple = batchEnabled && batchLocksSupported()

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; abortRef.current?.abort() }
  }, [])

  // Keep the screen on and warn before leaving while videos are uploading (PLAN-multivideo.md §15, §16).
  useEffect(() => {
    if (!running) return
    let lock: { release: () => Promise<void> } | null = null
    const wakeLock = (navigator as unknown as { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } }).wakeLock
    const acquire = () => { wakeLock?.request("screen").then((l) => { lock = l }).catch(() => { /* not allowed: carry on */ }) }
    const onVisible = () => { if (document.visibilityState === "visible") acquire() }
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = "" }
    acquire()
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("beforeunload", onBeforeUnload)
      lock?.release().catch(() => {})
    }
  }, [running])

  const patchItem = (id: number, patch: Partial<BatchItem>) => {
    if (!mounted.current) return
    setItems((list) => list.map((i) => (i.id === id ? { ...i, ...patch } : i)))
  }

  const deps = (): BatchDeps => {
    const upload = browserVideoUploadDeps()
    return {
      uploadOne: (file, o) => {
        // Runs inside the file lock: only here may "upload anyway" drop the remembered session (review mv-r1 #1).
        if (o.forceNew) forgetVideo(upload, accessKey, file)
        return uploadVideo(upload, {
          file, basePath, accessKey, message, pillarName, durationSec: o.durationSec, signal: o.signal,
          onProgress: ({ sentBytes }) => o.onProgress(sentBytes),
        })
      },
      readDuration: (file, signal) => new Promise((resolve) => {
        if (signal.aborted) return resolve(null)
        signal.addEventListener("abort", () => resolve(null), { once: true })
        readVideoDuration(file).then(resolve)
      }),
      withLock: browserWithLock,
      batchAllowed: batchAllowed ?? (async () => true),
      sleep: abortableSleep,
      random: Math.random,
    }
  }

  const runOrder = async (list: BatchItem[], order: number[]) => {
    if (!order.length || running) return
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setRunning(true)
    setStop(null)
    setNotice(null)
    let result: BatchStop | "held" = { kind: "completed" }
    let arrived = false
    try {
      result = await browserWithLock(`hotel-video-batch:${accessKey}`, () => runVideoBatch(list, deps(), {
        accessKey, order, signal: ctrl.signal,
        onItem: (id, patch) => { if (patch.status === "done") arrived = true; patchItem(id, patch) },
        onState: (s) => { if (mounted.current) setState(s) },
      }))
    } finally {
      abortRef.current = null
    }
    if (!mounted.current) return
    setRunning(false)
    setState(null)
    // Every new selection or retry needs a fresh confirmation (Mike 2026-10-30).
    setConsent(false)
    if (result === "held") {
      // Paused, not lost: the selection stays and "Weiter" sends it once the other window is done (review mv-r1 #3).
      setItems((l) => l.map((i) => (order.includes(i.id) && i.status === "waiting" ? { ...i, status: "not_started", reason: undefined } : i)))
      setStop({ kind: "paused", message: "In einem anderen Fenster läuft schon ein Upload. Wenn er fertig ist, Einwilligung bestätigen und auf „Weiter“ tippen." })
      return
    }
    setStop(result)
    if (arrived) onDone?.()
  }

  const start = (files: FileList | null) => {
    const picked = files ? Array.from(files) : []
    if (recordRef.current) recordRef.current.value = ""
    if (pickRef.current) pickRef.current.value = ""
    if (!picked.length || running) return
    if (!consent) { setNotice("Bitte bestätigen Sie zuerst die Einwilligung."); return }
    pruneDoneVideos(browserVideoUploadDeps(), accessKey)
    const list = prepareSelection(picked, multiple ? VIDEO_BATCH_MAX_FILES : 1)
    setItems(list)
    void runOrder(list, list.filter((i) => i.status === "waiting").map((i) => i.id))
  }

  const retry = () => {
    if (!consent) { setNotice("Bitte bestätigen Sie zuerst die Einwilligung."); return }
    const order = retryOrder(items)
    const list = items.map((i) => (order.includes(i.id) ? { ...i, status: "waiting" as const, reason: undefined, sentBytes: 0 } : i))
    setItems(list)
    void runOrder(list, order)
  }

  const uploadAnyway = (item: BatchItem) => {
    if (!consent) { setNotice("Bitte bestätigen Sie zuerst die Einwilligung."); return }
    const list = items.map((i) => (i.id === item.id ? { ...i, status: "waiting" as const, reason: undefined, sentBytes: 0, forceNew: true } : i))
    setItems(list)
    void runOrder(list, [item.id])
  }

  const discard = async (item: BatchItem) => {
    setBusyRow(item.id)
    const outcome = await browserWithLock(videoFileKey(accessKey, item.file),
      () => discardVideo(browserVideoUploadDeps(), { file: item.file, basePath, accessKey }).catch(() => "retry" as const))
    if (!mounted.current) return
    setBusyRow(null)
    if (outcome === "discarded") patchItem(item.id, { status: "skipped", reason: "Verworfen", open: false, sentBytes: 0 })
    else if (outcome === "arrived") patchItem(item.id, { status: "done", reason: undefined, open: false, sentBytes: item.file.size })
    else if (outcome === "unknown") patchItem(item.id, { reason: "Noch nicht bestätigt. Bitte „Erneut versuchen“." })
    else if (outcome === "held") patchItem(item.id, { reason: "Läuft schon in einem anderen Fenster" })
    else patchItem(item.id, { reason: outcome === "busy" ? "Gerade beschäftigt, bitte gleich erneut verwerfen." : "Verbindung prüfen und erneut verwerfen." })
  }

  const runIds = new Set(items.filter((i) => i.status !== "skipped").map((i) => i.id))
  const totalBytes = items.filter((i) => runIds.has(i.id)).reduce((s, i) => s + i.file.size, 0)
  const sentBytes = items.filter((i) => runIds.has(i.id)).reduce((s, i) => s + (["done", "already"].includes(i.status) ? i.file.size : i.sentBytes), 0)
  const canRetry = !running && items.some((i) => ["failed", "cancelled", "not_started"].includes(i.status))
  const isWaiting = state?.kind === "waiting"

  return (
    <div className="min-w-0 space-y-4 rounded-lg border p-4" data-testid="video-upload-panel">
      <div className="flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <p>{GUEST_HINT}</p>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id="video-consent"
          checked={consent}
          onCheckedChange={(v) => { setConsent(v === true); setNotice(null) }}
          disabled={running}
          data-testid="checkbox-video-consent"
        />
        <Label htmlFor="video-consent" className="text-sm leading-snug">
          {multiple ? "Erkennbare Personen in den gewählten Videos haben zugestimmt." : "Erkennbare Personen haben zugestimmt."}
        </Label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button type="button" variant="outline" disabled={!consent || running} onClick={() => recordRef.current?.click()} data-testid="button-video-record">
          <Video className="h-4 w-4 mr-2" aria-hidden="true" /> Video aufnehmen
        </Button>
        <Button type="button" variant="outline" disabled={!consent || running} onClick={() => pickRef.current?.click()} data-testid="button-video-pick">
          <FolderOpen className="h-4 w-4 mr-2" aria-hidden="true" /> {multiple ? "Videos wählen" : "Video wählen"}
        </Button>
        <input ref={recordRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => start(e.target.files)} data-testid="input-video-record" />
        <input ref={pickRef} type="file" accept="video/*" multiple={multiple} className="hidden" onChange={(e) => start(e.target.files)} data-testid="input-video-pick" />
      </div>
      <p className="text-xs text-muted-foreground">
        {multiple ? `Bis zu ${VIDEO_BATCH_MAX_FILES} Videos auf einmal, je max. 3 Minuten und 1 GB.` : "Max. 3 Minuten und 1 GB."} Am besten in 1080p filmen.
      </p>

      {notice && (
        <div className="flex items-center gap-2 text-sm text-destructive" role="alert" data-testid="video-upload-notice">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {notice}
        </div>
      )}

      {running && state && (
        <div className="space-y-2" data-testid="video-upload-progress" aria-live="polite">
          <div className="flex justify-between gap-2 text-sm">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span data-testid="video-upload-label">Video {state.current} von {state.total}</span>
            </span>
            <span>{mb(sentBytes)} / {mb(totalBytes)}</span>
          </div>
          <Progress value={(sentBytes / Math.max(1, totalBytes)) * 100} />
          {isWaiting && (
            <p className="text-xs text-muted-foreground" data-testid="video-upload-waiting">
              {state.why === "busy" ? "Gerade viel los" : state.why === "open" ? "Andere Uploads laufen noch" : "Kurze Pause wegen des Upload-Limits"}, es geht in etwa {state.seconds} Sekunden automatisch weiter.
            </p>
          )}
          <p className="text-xs text-muted-foreground">Bitte den Bildschirm anlassen, bis alle Videos hochgeladen sind.</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => abortRef.current?.abort()} data-testid="button-video-cancel">Abbrechen</Button>
        </div>
      )}

      {items.length > 0 && (
        <ul className="min-w-0 space-y-2" data-testid="video-upload-list">
          {items.map((item) => (
            <li key={item.id} className="min-w-0 rounded-md border px-3 py-2 text-sm" data-testid={`video-row-${item.status}`}>
              <div className="flex min-w-0 items-center justify-between gap-2">
                <span className="min-w-0 break-all line-clamp-2 font-medium" title={item.file.name}>{item.file.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{mb(item.file.size)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs">
                <RowIcon status={item.status} />
                <span className={item.status === "failed" ? "text-destructive" : "text-muted-foreground"}>
                  {item.status === "uploading"
                    ? `${STATUS_TEXT.uploading} ${Math.floor((item.sentBytes / Math.max(1, item.file.size)) * 100)} %`
                    : STATUS_TEXT[item.status]}
                  {item.reason && item.status !== "already" ? `: ${item.reason}` : ""}
                </span>
              </div>
              {item.status === "uploading" && <Progress className="mt-2 h-1.5" value={(item.sentBytes / Math.max(1, item.file.size)) * 100} />}
              {!running && item.status === "failed" && item.open && (
                <Button type="button" variant="ghost" size="sm" className="h-auto px-0 py-1 text-xs underline underline-offset-2" disabled={busyRow === item.id} onClick={() => discard(item)} data-testid="button-video-discard">
                  Verwerfen
                </Button>
              )}
              {!running && item.status === "already" && (
                <Button type="button" variant="ghost" size="sm" className="h-auto px-0 py-1 text-xs underline underline-offset-2" disabled={!consent} onClick={() => uploadAnyway(item)} data-testid="button-video-anyway">
                  Trotzdem hochladen
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!running && stop && (
        <div className="space-y-2 text-sm" data-testid="video-upload-summary">
          <p className="font-medium" role="status">{summarizeBatch(items)}</p>
          {(stop.kind === "paused" || stop.kind === "stopped") && <p className="text-muted-foreground">{stop.message}</p>}
          {canRetry && stop.kind !== "stopped" && (
            <div className="space-y-1">
              <Button type="button" variant="outline" size="sm" disabled={!consent} onClick={retry} data-testid="button-video-retry">
                {stop.kind === "paused" ? "Weiter" : "Fehlgeschlagene erneut versuchen"}
              </Button>
              {!consent && <p className="text-xs text-muted-foreground">Dafür zuerst oben die Einwilligung bestätigen.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RowIcon({ status }: { status: BatchItem["status"] }) {
  if (status === "done" || status === "already") return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden="true" />
  if (status === "failed") return <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
  if (status === "uploading" || status === "checking") return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
  return <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
}
